import { HttpErrorResponse } from '@angular/common/http';

import { AppConfig } from 'src/shared/providers/config';

export interface ApiFailure {
  status: number;
  method?: string;
  url: string;
}

/** What a failed API request is reported to Sentry as: status, method, endpoint and the API's message. */
export class ApiRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiRequestError';
    // The stack would only show the error handler, the same for every failure, and Sentry groups by stack
    // when there is one. Without it, Sentry groups by this title, so each failing endpoint is its own issue.
    this.stack = '';
  }
}

const MAX_MESSAGE_LENGTH = 200;
const VERSION_SEGMENT = /^v\d+$/;
const SIGN_IN_SEGMENT = /^(login|password-recovery)$/;
const EMAIL = /[^\s@'"]+@[^\s@'"]+\.[^\s@'"]+/g;
const URL_IN_TEXT = /https?:\/\/\S+/g;
const TOKEN = /(?=[\w.~+/=-]*\d)[\w.~+/=-]{16,}/g;
const NUMBER = /\+?\d[\d\s().:-]*\d|\d/g;

// HttpClient rejects with the API's error body, which says nothing of the request it came from.
const failures = new WeakMap<object, ApiFailure>();

/** Remembers the request an API error body came from, so its Sentry report can name it. */
export function recordApiFailure(rejection: unknown, failure: ApiFailure): void {
  if (rejection && typeof rejection === 'object' && !(rejection instanceof Error)) {
    failures.set(rejection, failure);
  }
}

/**
 * Extractor for `Sentry.createErrorHandler()`. A failed API request becomes an `ApiRequestError` titled
 * like `404 GET /api/v1/homekeeper/cleanings/:id/moments: Couldn't find Cleaning`. Anything else goes to
 * Sentry's own extractor, so real Errors are reported as before.
 */
export function sentryErrorExtractor(error: unknown, defaultExtractor: (error: unknown) => unknown): unknown {
  const candidate = (error as { ngOriginalError?: unknown })?.ngOriginalError ?? error;

  if (candidate instanceof HttpErrorResponse) {
    const body = candidate.error?.error ?? candidate.error;
    return apiRequestError({ status: candidate.status, url: candidate.url ?? '' }, body);
  }

  const failure = candidate && typeof candidate === 'object' ? failures.get(candidate) : undefined;
  return failure ? apiRequestError(failure, candidate) : defaultExtractor(error);
}

function apiRequestError(failure: ApiFailure, body: unknown): ApiRequestError {
  const request = [failure.status, failure.method, endpoint(failure.url)].filter(part => part !== undefined).join(' ');
  const apiMessage = (body as { message?: unknown })?.message;
  const message = typeof apiMessage === 'string' && apiMessage.trim() ? `${request}: ${scrubMessage(apiMessage)}` : request;
  return new ApiRequestError(message);
}

/** The request's path with ids and keys replaced, without its query string, and its host when it isn't the API. */
export function endpoint(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url, AppConfig.API);
  } catch {
    return ':url';
  }

  const segments = parsed.pathname.split('/');
  const path = segments.map((segment, index) => {
    if (index > 0 && SIGN_IN_SEGMENT.test(segments[index - 1]) && segment) {
      return ':token';
    }
    return isId(segment) ? ':id' : segment;
  }).join('/');

  return parsed.host === new URL(AppConfig.API).host ? path : `${parsed.host}${path}`;
}

function isId(segment: string): boolean {
  return !VERSION_SEGMENT.test(segment) && (/\d/.test(segment) || segment.includes('@') || segment.length > 30);
}

/** The API's message without addresses, emails, keys or numbers, so the same failure always reads the same. */
export function scrubMessage(message: string): string {
  const scrubbed = message
    .replace(URL_IN_TEXT, ':url')
    .replace(EMAIL, ':email')
    .replace(TOKEN, ':token')
    .replace(NUMBER, ':number')
    .trim();
  return scrubbed.length > MAX_MESSAGE_LENGTH ? `${scrubbed.slice(0, MAX_MESSAGE_LENGTH)}…` : scrubbed;
}
